import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  Query,
  query,
  setDoc,
  updateDoc,
  limit,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

export async function getItems<T>(
  accountId: string,
  collectionName: string,
  filterFn?: (_query: Query) => Query,
  addId?: boolean,
): Promise<T[] | undefined> {
  const collectionRef = collection(db, collectionName);
  try {
    const q = filterFn
      ? filterFn(query(collectionRef, where('accountId', '==', accountId)))
      : query(collectionRef, where('accountId', '==', accountId));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return;
    }

    return addId
      ? querySnapshot.docs.map((doc) => ({ ...(doc.data() as T), id: doc.id }))
      : querySnapshot.docs.map((doc) => doc.data() as T);
  } catch (error) {
    console.error('Error getting documents:', error);
  }
}

export async function getAllItems<T>(
  collectionName: string,
  filterFn?: (_query: Query) => Query,
  addId?: boolean,
): Promise<T[] | undefined> {
  const collectionRef = collection(db, collectionName);
  try {
    const q = filterFn ? filterFn(query(collectionRef)) : query(collectionRef);

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return;
    }

    return addId
      ? querySnapshot.docs.map((doc) => ({ ...(doc.data() as T), id: doc.id }))
      : querySnapshot.docs.map((doc) => doc.data() as T);
  } catch (error) {
    console.error('Error getting documents:', error);
  }
}

export async function adminGetItems<T>(collectionName: string): Promise<(T & { firebaseId: string })[] | undefined> {
  const collectionRef = collection(db, collectionName);
  try {
    const q = query(collectionRef, limit(200));
    const querySnapshot = await getDocs(q);
    return (
      (!querySnapshot.empty &&
        querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id } as T & { firebaseId: string }))) ||
      undefined
    );
  } catch (error) {
    console.error('Error getting documents:', error);
  }
}

export async function saveItem<T>(itemId: string, itemData: T, collectionName: string): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, itemId);
    await setDoc(docRef, itemData as any).catch((e) => console.log('error creating ', e));
    const docSnap = await getDoc(docRef);
    return docSnap.data() as T;
  } catch (err) {
    console.error(`error found! ${err}`);
    return null;
  }
}

export async function updateItem<T>(itemId: string, itemData: Partial<T>, collectionName: string): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, itemId);
    await updateDoc(docRef, itemData as any).catch((e) => console.log('error updating ', e));
    const docSnap = await getDoc(docRef);
    return docSnap.data() as T;
  } catch (err) {
    console.error(`error found! ${err}`);
    return null;
  }
}

export async function saveItemGenerateId<T>(itemData: T, collectionName: string): Promise<T | null> {
  try {
    const docRef = await addDoc(collection(db, collectionName), itemData as any);
    const docSnap = await getDoc(docRef);
    return { ...docSnap.data(), id: docRef.id } as T;
  } catch (err) {
    console.error(`error found! ${err}`);
    return null;
  }
}

export async function getItem<T>(itemId: string, collectionName: string): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, itemId);
    const docSnap = await getDoc(docRef);
    return (docSnap.exists() && (docSnap.data() as T)) || null;
  } catch (err) {
    console.error(`error found! ${err}`);
    return null;
  }
}

export async function deleteItem(id: string, collectionName: string): Promise<undefined> {
  try {
    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);
    return;
  } catch (err) {
    console.error(`Error deleting ${err}`);
  }
}

export async function deleteItemsByIds(ids: string[], collectionName: string): Promise<void> {
  try {
    const batch = writeBatch(db);

    ids.forEach((id) => {
      const docRef = doc(db, collectionName, id);
      batch.delete(docRef);
    });

    await batch.commit();
  } catch (err) {
    console.error(`Error deleting documents: ${err}`);
  }
}
